import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import FormHandler from "../../shared/utils/FormHandler";
import useBlogApi from "../../apis/blogApi";
import useAuthorApi from "../../apis/authorApi";
import { validateBlogUpdate } from "../../utils/FormValidationRules";
import { deriveStatus } from "../../utils/blogStatus";
import BlogEditor from "./BlogEditor";
import "./blog-editor.css";
import "./blog-editor-page.css";

const TECHNOLOGY_OPTIONS = [
  "ML",
  "Bot Service",
  "Auto ML",
  "Big Data Analytics",
  "IoT",
  "Edge Computing",
  "Software",
  "Computer Vision",
];

const PRODUCT_OPTIONS = [
  { value: "senzagro", label: "SenzAgro" },
  { value: "senzmatica", label: "SenzMatica" },
  { value: "devicepulse", label: "DevicePulse.Ai" },
  { value: "polar", label: "Polar" },
  { value: "senzcare", label: "Senzcare.Ai" },
  { value: "senzview", label: "SenzView" },
  { value: "factoryspy", label: "FactorySpy" },
  { value: "solartrack", label: "SolarTrack" },
  { value: "mosurance", label: "Mosurance" },
  { value: "tourismbot", label: "Tourism Bot" },
];

const INDUSTRY_OPTIONS = [
  { value: "medtech", label: "MedTech" },
  { value: "finance", label: "Finance" },
  { value: "tourism", label: "Tourism" },
  { value: "hospitality", label: "Hospitality" },
  { value: "sports", label: "Sports" },
  { value: "agriculture", label: "Agriculture" },
  { value: "retail", label: "Retail" },
];

const SOCIAL_OPTIONS = [
  { value: "twitter", label: "Twitter" },
  { value: "tiktok", label: "TikTok" },
  { value: "youtube", label: "YouTube" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
];

const emptyRelatedPost = () => ({
  article_id: "",
  title: "",
  subtitle: "",
  image: "",
  publishedAt: "",
  author_name: "",
});

const emptySocialLink = () => ({
  option: "",
  type: "",
  title: "",
  description: "",
  image: "",
  link: "",
});

const BlogEditorPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const isUpdate = Boolean(id);

  const { blogs, editBlog, addBlog, uploadBlogImage, fetchArticleContent } = useBlogApi();
  const { authors } = useAuthorApi();
  const { values, errors, handleChange, handleOnBlur, setValue, initForm } =
    FormHandler(editBlog, validateBlogUpdate);

  const [submitErrors, setSubmitErrors] = useState({});
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [isUploadingFeaturedImage, setIsUploadingFeaturedImage] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Edit mode waits for the list to load, then seeds the form from the
  // matching record — mirrors the old modal's handleEditClick field mapping.
  useEffect(() => {
    if (!isUpdate || initialized) return;
    const blog = blogs.find((b) => b._id === id);
    if (!blog || !blog.attributes) {
      // Once the list has actually loaded and still has no match, this id is
      // stale (deleted post, bad link) — don't leave a blank form forever.
      if (blogs.length > 0) history.push("/blogs");
      return;
    }

    const technologies = blog.attributes.technology || [];

    initForm({
      article_id: blog.attributes.article_id,
      id: blog._id,
      title: blog.attributes.title,
      subtitle: blog.attributes.subtitle,
      language: blog.attributes.language,
      status: deriveStatus(blog.attributes),
      app: blog.attributes.app,
      type: blog.attributes.type,
      image: blog.attributes.image,
      product: blog.attributes.product,
      industry: blog.attributes.industry,
      author_id: blog.attributes.author_id || "",
      technology: technologies,
      published_date: blog.attributes.published_date || "",
      content_html: "", // fetched separately below — GET /articles no longer carries it
      featured_image_url: blog.attributes.featured_image_url || "",
      seo: blog.attributes.seo || {},
    });
    setRelatedPosts(blog.attributes.related_posts || []);
    setSocialLinks(blog.attributes.social_media_links || []);
    setInitialized(true);

    fetchArticleContent(blog.attributes.article_id).then((html) => {
      setValue({ content_html: html });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate, initialized, blogs, id]);

  useEffect(() => {
    if (!isUpdate) {
      initForm({ status: "draft" });
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => history.push("/blogs");

  const toggleTechnology = (name) => {
    const current = values.technology || [];
    const next = current.includes(name)
      ? current.filter((t) => t !== name)
      : [...current, name];
    setValue({ technology: next });
  };

  const handleFeaturedImagePick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;

    setIsUploadingFeaturedImage(true);
    try {
      const url = await uploadBlogImage(file);
      setValue({ featured_image_url: url });
    } finally {
      setIsUploadingFeaturedImage(false);
    }
  };

  const handleRelatedPostChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...relatedPosts];
    updated[index] = { ...updated[index], [name]: value };
    setRelatedPosts(updated);
  };

  const handleSocialLinkChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...socialLinks];
    updated[index] = { ...updated[index], [name]: value };
    setSocialLinks(updated);
  };

  const handleSubmit = (statusOverride) => (e) => {
    if (e) e.preventDefault();

    const submitValues = statusOverride ? { ...values, status: statusOverride } : values;
    const formErrors = validateBlogUpdate(submitValues);
    setSubmitErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;

    const nonEmptyRelated = relatedPosts.filter((p) =>
      Object.values(p).some((v) => (v || "").toString().trim() !== "")
    );
    const nonEmptySocial = socialLinks.filter((p) =>
      Object.values(p).some((v) => (v || "").toString().trim() !== "")
    );

    const payload = {
      ...submitValues,
      related_posts: nonEmptyRelated,
      social_media_links: nonEmptySocial,
    };

    if (isUpdate) {
      editBlog(payload);
    } else {
      addBlog(payload);
    }
    history.push("/blogs");
  };

  return (
    <div className="bep-page">
      <div className="bep-topbar">
        <button type="button" className="bep-back-btn" onClick={goBack} aria-label="Back to blog posts">
          <FeatherIcon icon="arrow-left" size={18} />
        </button>
        <div className="bep-topbar-divider" />
        <span className="bep-topbar-title">{isUpdate ? "Edit Blog Post" : "New Blog Post"}</span>
        <div className="bep-topbar-spacer" />

        <div className="bep-status-group" role="radiogroup" aria-label="Status">
          {["draft", "published", "archived"].map((status) => (
            <label key={status}>
              <input
                type="radio"
                name="status"
                value={status}
                checked={(values.status || "draft") === status}
                onChange={() => setValue({ status })}
                className="bep-status-pill-input"
              />
              <span className="bep-status-pill">
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </label>
          ))}
        </div>

        <button type="button" className="bep-btn bep-btn-secondary" onClick={handleSubmit("draft")}>
          Save Draft
        </button>
        <button type="button" className="bep-btn bep-btn-primary" onClick={handleSubmit("published")}>
          {isUpdate && deriveStatus(values) === "published" ? "Update" : "Publish"}
        </button>
      </div>

      <div className="bep-body">
        <form className="bep-body-inner" onSubmit={handleSubmit()}>
          <div className="bep-main">
            {isUpdate && values.article_id && (
              <div className="bep-url-row">
                <FeatherIcon icon="link-2" size={13} />
                <span>/blogs/{values.article_id}</span>
              </div>
            )}

            <input
              type="text"
              className="bep-title-input"
              placeholder="Untitled — click to add a title"
              name="title"
              value={values.title || ""}
              onChange={handleChange}
              onBlur={handleOnBlur}
            />
            {(errors.title || submitErrors.title) && (
              <p className="bep-error-text">{errors.title || submitErrors.title}</p>
            )}

            <textarea
              className="bep-subtitle-input"
              placeholder="Add a subtitle…"
              name="subtitle"
              rows={2}
              value={values.subtitle || ""}
              onChange={handleChange}
              onBlur={handleOnBlur}
            />
            {(errors.subtitle || submitErrors.subtitle) && (
              <p className="bep-error-text">{errors.subtitle || submitErrors.subtitle}</p>
            )}

            <div className="bep-header-divider" />

            <div>
              <label className="bep-field-label" htmlFor="content_html">Content</label>
              <BlogEditor
                value={values.content_html}
                onChange={(html) => setValue({ content_html: html })}
                onUploadImage={uploadBlogImage}
              />
              {(errors.content_html || submitErrors.content_html) && (
                <p className="bep-error-text">{errors.content_html || submitErrors.content_html}</p>
              )}
            </div>

            <div className="bep-panel">
              <div className="bep-section-head">
                <h3 className="bep-section-title">Related Posts</h3>
                <button
                  type="button"
                  className="bep-add-btn"
                  onClick={() => setRelatedPosts([...relatedPosts, emptyRelatedPost()])}
                >
                  <FeatherIcon icon="plus" size={14} /> Add
                </button>
              </div>
              {relatedPosts.length === 0 && (
                <p className="bep-empty-hint">No related posts linked.</p>
              )}
              {relatedPosts.map((post, index) => (
                <div className="bep-repeatable-row" key={index}>
                  <div className="bep-repeatable-grid">
                    <div className="bep-field">
                      <label className="bep-field-label">Article ID</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="article_id"
                        value={post.article_id}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      />
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Title</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="title"
                        value={post.title}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      />
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Description</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="subtitle"
                        value={post.subtitle}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      />
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Image URL</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="image"
                        value={post.image}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      />
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Social Media</label>
                      <select
                        className="bep-select"
                        name="socialmedia"
                        value={post.socialmedia || ""}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      >
                        <option value="" disabled>Select</option>
                        {SOCIAL_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Published At</label>
                      <input
                        type="date"
                        className="bep-input"
                        name="publishedAt"
                        value={post.publishedAt}
                        onChange={(e) => handleRelatedPostChange(index, e)}
                      />
                    </div>
                  </div>
                  <div className="bep-repeatable-remove">
                    <button
                      type="button"
                      className="bep-link-btn"
                      onClick={() => setRelatedPosts(relatedPosts.filter((_, i) => i !== index))}
                    >
                      <FeatherIcon icon="trash-2" size={13} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="bep-panel">
              <div className="bep-section-head">
                <h3 className="bep-section-title">Related Social Media</h3>
                <button
                  type="button"
                  className="bep-add-btn"
                  onClick={() => setSocialLinks([...socialLinks, emptySocialLink()])}
                >
                  <FeatherIcon icon="plus" size={14} /> Add
                </button>
              </div>
              {socialLinks.length === 0 && (
                <p className="bep-empty-hint">No social links added.</p>
              )}
              {socialLinks.map((post, index) => (
                <div className="bep-repeatable-row" key={index}>
                  <div className="bep-repeatable-grid">
                    <div className="bep-field">
                      <label className="bep-field-label">Option</label>
                      <select
                        className="bep-select"
                        name="option"
                        value={post.option}
                        onChange={(e) => handleSocialLinkChange(index, e)}
                      >
                        <option value="" disabled>Select</option>
                        <option value="socialMedia">Social Media</option>
                        <option value="weblink">Web Link</option>
                      </select>
                    </div>
                    {post.option === "socialMedia" && (
                      <div className="bep-field">
                        <label className="bep-field-label">Type</label>
                        <select
                          className="bep-select"
                          name="type"
                          value={post.type}
                          onChange={(e) => handleSocialLinkChange(index, e)}
                        >
                          <option value="" disabled>Select</option>
                          {SOCIAL_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="bep-field">
                      <label className="bep-field-label">Title</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="title"
                        value={post.title}
                        onChange={(e) => handleSocialLinkChange(index, e)}
                      />
                    </div>
                    {post.option === "weblink" && (
                      <div className="bep-field">
                        <label className="bep-field-label">Image URL</label>
                        <input
                          type="text"
                          className="bep-input"
                          name="image"
                          value={post.image}
                          onChange={(e) => handleSocialLinkChange(index, e)}
                        />
                      </div>
                    )}
                    <div className="bep-field">
                      <label className="bep-field-label">Description</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="description"
                        value={post.description || ""}
                        onChange={(e) => handleSocialLinkChange(index, e)}
                      />
                    </div>
                    <div className="bep-field">
                      <label className="bep-field-label">Link</label>
                      <input
                        type="text"
                        className="bep-input"
                        name="link"
                        value={post.link}
                        onChange={(e) => handleSocialLinkChange(index, e)}
                      />
                    </div>
                  </div>
                  <div className="bep-repeatable-remove">
                    <button
                      type="button"
                      className="bep-link-btn"
                      onClick={() => setSocialLinks(socialLinks.filter((_, i) => i !== index))}
                    >
                      <FeatherIcon icon="trash-2" size={13} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bep-sidebar">
            <div className="bep-panel">
              <h3 className="bep-panel-title">Featured Image</h3>
              {values.featured_image_url && (
                <img src={values.featured_image_url} alt="Featured" className="bep-image-preview" />
              )}
              <label className="bep-image-drop">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={handleFeaturedImagePick}
                  disabled={isUploadingFeaturedImage}
                />
                {isUploadingFeaturedImage
                  ? "Uploading…"
                  : values.featured_image_url
                    ? "Replace image"
                    : "Click to upload an image"}
              </label>
            </div>

            <div className="bep-panel">
              <h3 className="bep-panel-title">Details</h3>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="app">App</label>
                <select
                  id="app"
                  className={"bep-select" + ((errors.app || submitErrors.app) ? " bep-input-error" : "")}
                  name="app"
                  value={values.app || ""}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                >
                  <option value="" disabled>Select</option>
                  <option value="senzmate">SenzMate</option>
                  <option value="senzagro">SenzAgro</option>
                </select>
                {(errors.app || submitErrors.app) && (
                  <p className="bep-error-text">{errors.app || submitErrors.app}</p>
                )}
              </div>
              <div className="bep-field-row">
                <div className="bep-field">
                  <label className="bep-field-label" htmlFor="type">Type</label>
                  <select
                    id="type"
                    className="bep-select"
                    name="type"
                    value={values.type || ""}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                  >
                    <option value="" disabled>Select</option>
                    <option value="BLOGS">Blogs</option>
                    <option value="CASE_STUDIES">Case Studies</option>
                    <option value="TECH_BLOGS">Tech Blogs</option>
                    <option value="SUCCESS_STORIES">Success Stories</option>
                  </select>
                </div>
                <div className="bep-field">
                  <label className="bep-field-label" htmlFor="language">Language</label>
                  <select
                    id="language"
                    className="bep-select"
                    name="language"
                    value={values.language || ""}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                  >
                    <option value="" disabled>Select</option>
                    <option value="ENG">ENG</option>
                    <option value="JAP">JAP</option>
                  </select>
                </div>
              </div>
              <div className="bep-field">
                <div className="bep-section-head">
                  <label className="bep-field-label" htmlFor="author_id">Written by</label>
                  <a href="/authors" target="_blank" rel="noopener noreferrer" className="bep-link-btn">
                    <FeatherIcon icon="plus" size={13} /> New author
                  </a>
                </div>
                <select
                  id="author_id"
                  className="bep-select"
                  name="author_id"
                  value={values.author_id || ""}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                >
                  <option value="">Select an author…</option>
                  {authors.map((author) => (
                    <option key={author._id} value={author._id}>{author.name}</option>
                  ))}
                </select>
                <p className="bep-field-hint">
                  Not listed? Add them in a new tab, then refresh this page.
                </p>
              </div>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="published_date">Published Date</label>
                <input
                  id="published_date"
                  type="date"
                  className="bep-input"
                  value={values.published_date || ""}
                  onChange={(e) => setValue({ published_date: e.target.value })}
                />
              </div>
            </div>

            <div className="bep-panel">
              <h3 className="bep-panel-title">Categorize</h3>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="product">Product</label>
                <select
                  id="product"
                  className="bep-select"
                  name="product"
                  value={values.product || ""}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                >
                  <option value="" disabled>Select</option>
                  {PRODUCT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="industry">Industry</label>
                <select
                  id="industry"
                  className="bep-select"
                  name="industry"
                  value={values.industry || ""}
                  onChange={handleChange}
                  onBlur={handleOnBlur}
                >
                  <option value="" disabled>Select</option>
                  {INDUSTRY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="bep-field">
                <label className="bep-field-label">Technology</label>
                <div className="bep-chip-group">
                  {TECHNOLOGY_OPTIONS.map((name) => (
                    <button
                      type="button"
                      key={name}
                      className={"bep-chip" + ((values.technology || []).includes(name) ? " is-selected" : "")}
                      onClick={() => toggleTechnology(name)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bep-panel">
              <h3 className="bep-panel-title">SEO</h3>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="seo_meta_title">Meta Title</label>
                <input
                  id="seo_meta_title"
                  type="text"
                  className="bep-input"
                  placeholder={values.title || ""}
                  value={(values.seo && values.seo.meta_title) || ""}
                  onChange={(e) => setValue({ seo: { ...values.seo, meta_title: e.target.value } })}
                />
              </div>
              <div className="bep-field">
                <label className="bep-field-label" htmlFor="seo_meta_description">Meta Description</label>
                <textarea
                  id="seo_meta_description"
                  className="bep-textarea"
                  rows={3}
                  placeholder={values.subtitle || ""}
                  value={(values.seo && values.seo.meta_description) || ""}
                  onChange={(e) => setValue({ seo: { ...values.seo, meta_description: e.target.value } })}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogEditorPage;
